import React, { useEffect, useContext } from 'react';
import { get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';
import clsx from 'clsx';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { serialActions, consultationReasonsActions } from '@docavenue/core';
import { useRouter } from 'next/router';
import { useTranslation } from '@/src/i18n';
import ProgressiveForm from './ProgressiveForm';
import SpecialitySelect from '../../molecules/Formik/SpecialitySelect';
import ConsultationReasonSelect from '../../molecules/Formik/ConsultationReasonSelect';
import CenterSelect from '../../molecules/Formik/CenterSelect';
import PractitionerSelect from '../../molecules/Formik/PractitionerSelect';
import {
  temporaryAppointmentsActions,
  appointmentSpecialitiesActions,
  consultationReasonsByNameActions,
  practitionersActions,
  availabilitiesActions,
} from '../../../src/actions';
import HCDCardContext from '../../templates/HCDCard/HCDCardContext';
import AvailabilityAppointment from '../../molecules/Formik/AvailabilityAppointment';
import ConsultationTypeChoice from '../../molecules/Formik/ConsultationTypeChoice';
import { DynamicField } from './DynamicForm';
import { useIsPartners } from '@/src/hooks/useIsPartners';
import { isNoPractitionerSelected } from '../../utils/card';
import { APP_ROUTES } from '@/src/constants';

type Props = {
  card: CardHcd;
};

const AppointmentForm = ({ card }: Props) => {
  const { t } = useTranslation();
  const { type } = card;
  const dispatch = useDispatch();
  const router = useRouter();
  const hcdCardContext = useContext(HCDCardContext);
  const isPartners = useIsPartners();
  useEffect(
    () => () => {
      practitionersActions.getActions().reset();
      [
        appointmentSpecialitiesActions,
        consultationReasonsActions.getActions(),
        consultationReasonsByNameActions,
        availabilitiesActions,
      ].forEach(action => {
        dispatch(action.reset());
      });
    },
    [],
  );

  const fields: DynamicField[] = [];
  const initialValues: Record<string, any> = {
    rootCenterId: card?.center?.id,
    type: undefined,
    consultationReasonName: undefined,
  };

  switch (type) {
    case 'PRACTITIONER': {
      fields.push(
        {
          name: 'consultationReasonName',
          label: t('form_rdv_consultation_reason'),
          component: ConsultationReasonSelect,
        },
        {
          name: 'centerId',
          label: t('form_rdv_consultation_place'),
          component: CenterSelect,
        },
        {
          name: 'type',
          label: 'Type de consultation',
          component: ConsultationTypeChoice,
        },
      );
      const practitioner = card?.professional;
      initialValues.practitionerId = practitioner?.practitionerId;
      initialValues.centerId = card?.center?.id; // TODO: implement multiple centers for practitioner
      break;
    }

    case 'CENTER':
      fields.push(
        {
          name: 'specialityId',
          label: t('form_rdv_consultation_specialty'),
          component: SpecialitySelect,
        },
        {
          name: 'consultationReasonName',
          label: t('form_rdv_consultation_reason'),
          component: ConsultationReasonSelect,
        },
        {
          name: 'centerId',
          label: t('form_rdv_consultation_center'),
          component: CenterSelect,
        },
        {
          name: 'practitionerId',
          label: t('form_rdv_consultation_practitioner'),
          component: PractitionerSelect,
        },
        {
          name: 'type',
          label: 'Type de consultation',
          component: ConsultationTypeChoice,
        },
      );
      initialValues.specialityId = '';
      break;
    default:
      break;
  }
  const bottomFields = [
    {
      name: 'availability',
      label: t('form_rdv_consultation_availability'),
      component: AvailabilityAppointment,
    },
  ];

  const consultationReasonByName = useSelector(state =>
    get(state, 'consultationReasonsByName.item'),
  );

  return (
    <VisibilitySensor offset={{ top: 57 }}>
      {({ isVisible }) => (
        <div
          className={clsx('rdv-form', {
            unstick: !isVisible,
          })}
          onClick={() => hcdCardContext?.onFormClick?.()}
        >
          <div className="rdv-form__overlay" />
          {isPartners ? (
            <div className="wrapper-headings">
              <h2 className="title">
                {t('common:ispartners_appointment_title')}
              </h2>
              <p>{t('common:ispartners_appointment_subtitle')}</p>
            </div>
          ) : (
            <div className="rdv-form__title">
              <h4>{t('get-online-rdv')}</h4>
              <span>{t('fill-form-rdv')}</span>
            </div>
          )}

          <ProgressiveForm
            type={type}
            initialValues={initialValues}
            fields={fields}
            /* MPAT-3973: Reset center to prevent the selected center dont have consulation */
            ignoreResetFields={[]}
            bottomFields={bottomFields}
            onSubmit={values => {
              (window as any).dataLayer?.push({
                event: 'AppointmentForm_onSubmit',
                center: card?.center,
                professional: card?.professional,
                type: card?.type,
              });
              const mergeValues =
                type === 'PRACTITIONER' ? { cardHcd: { ...card } } : {};
              dispatch(
                serialActions.serial([
                  state =>
                    temporaryAppointmentsActions.mergeItem({
                      ...values,
                      ...mergeValues,
                      consultationReason: {
                        name: get(values, 'consultationReasonName'),
                      },
                      startDate: get(values, 'availability.startDateTime'),
                      endDate: get(values, 'availability.endDateTime'),
                      _timestamp: new Date(), // For keep track storage temporaryAppointment to display continue schedule appointment when go to other pages
                      ...(isNoPractitionerSelected(values.practitionerId)
                        ? {
                            practitionerId:
                              state.temporaryAppointments.item.slot
                                .practitionerId,
                            consultationReasonId:
                              state.temporaryAppointments.item.slot
                                .consultationReasonId,
                          }
                        : {
                            consultationReasonId: consultationReasonByName.id,
                          }),
                    }),
                  () => router.push(APP_ROUTES.APPOINTMENT_CONFIRMATION),
                ]),
              );
            }}
            card={card}
          />
        </div>
      )}
    </VisibilitySensor>
  );
};

export default AppointmentForm;
